import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { CategoriesListComponent } from '../categories-list/categories-list.component';
import { GroupService } from '../../services/group.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { injectMutation, injectQuery, keepPreviousData } from '@tanstack/angular-query-experimental';
import { map, tap } from 'rxjs/operators';
import { NavigationService } from '../../services/navigation.service';
import { MonthService } from '../../services/month.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticsService } from '../../services/analytics.service';
import {
  EVENT_BUDGETS_PAGE_VIEWED,
  EVENT_CHANGED_BUDGET_AMOUNT,
  EVENT_OPENED_CATEGORY_SEARCH,
} from '../../analytics/events';
import { UserService } from '../../services/user.service';
import { Bucket, Group, GroupPage } from '../../types';
import { NgForm, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DrawerService } from '../../services/drawer.service';
import { CurrencyPipe } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { UiButtonDirective } from '../../ui/button/button.directive';
import { UiIconButtonComponent } from '../../ui/icon-button/icon-button.component';
import { CategoriesPageSummaryComponent } from '../categories-page-summary/categories-page-summary.component';
import { UiIconComponent } from '../../ui/icon/icon.component';
import { UiInputDirective } from '../../ui/input/input.directive';
import { AutofocusDirective } from '../../directives/autofocus.directive';
import { BudgetBulkEditService } from '../../services/budget-bulk-edit.service';
import { AppUpdateService } from '../../services/app-update.service';
import { NgIcon } from '@ng-icons/core';
import { HlmIcon } from '../../ui/helm/icon/src/lib/hlm-icon';
import {
  UiDropdownMenuTriggerDirective,
  UiDropdownMenuDirective,
  UiDropdownMenuItemDirective,
} from '../../ui/menu/menu.directive';
import { TransfersCardComponent } from '../transfers-card/transfers-card.component';
import { isOverBudgetBucket } from '../bucket-over-budget';
import { budgetVarianceDigits } from '../budget-variance-format';
import { TransactionService } from '../../services/transaction.service';
import {
  UiAlertActionDirective,
  UiAlertDescriptionDirective,
  UiAlertDirective,
  UiAlertIconDirective,
  UiAlertTitleDirective,
} from '../../ui/alert/alert.directive';
import { UiSaveButtonComponent } from '../../ui/save-button/save-button.component';
import { UiToastService } from '../../ui/toast/toast.service';
import { UiDialogService } from '../../ui/dialog/dialog.service';
import { ConfirmDialogComponent } from '../../dialog-templates/confirm-dialog/confirm-dialog.component';
import { ConfirmDialog } from '../../types/dialog';

@Component({
  selector: 'app-categories-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './categories-page.component.html',
  host: {
    '(document:keydown)': 'onDocumentKeydown($event)',
  },
  imports: [
    UiButtonDirective,
    UiIconButtonComponent,
    CategoriesPageSummaryComponent,
    UiIconComponent,
    UiInputDirective,
    ReactiveFormsModule,
    FormsModule,
    AutofocusDirective,
    CategoriesListComponent,
    UiAlertDirective,
    UiAlertIconDirective,
    UiAlertTitleDirective,
    UiAlertDescriptionDirective,
    UiAlertActionDirective,
    UiSaveButtonComponent,
    CurrencyPipe,
    NgIcon,
    HlmIcon,
    UiDropdownMenuTriggerDirective,
    UiDropdownMenuDirective,
    UiDropdownMenuItemDirective,
    TransfersCardComponent,
  ],
})
export class CategoriesPageComponent implements OnInit {
  private analytics = inject(AnalyticsService);
  groupService = inject(GroupService);
  private nav = inject(NavigationService);
  month = inject(MonthService);
  userService = inject(UserService);
  drawerService = inject(DrawerService);
  budgetBulkEdit = inject(BudgetBulkEditService);
  private readonly appUpdate = inject(AppUpdateService);
  private transactionService = inject(TransactionService);

  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toast = inject(UiToastService);
  private readonly dialog = inject(UiDialogService);
  private readonly searchOpen = signal(false);
  private routeDrivenEditMode = false;
  /**
   * Bumped on every edit-mode entry and exit. Drag-and-drop reorders the rendered
   * arrays in place, so each session needs fresh copies — otherwise a cancelled
   * reorder would keep showing until the next refetch.
   */
  private readonly editSession = signal(0);
  readonly saving = signal(false);
  readonly saveError = signal<string | null>(null);
  readonly groupSearch = signal(this.groupService.groupSearch);
  readonly currentMonthYear = signal(this.month.monthYearFromDate());
  private readonly requestedEditMode = toSignal(this.route.queryParamMap.pipe(map(params => params.get('edit'))), {
    initialValue: this.route.snapshot.queryParamMap.get('edit'),
  });

  readonly categoriesList = viewChild(CategoriesListComponent);

  // Keep the last month on screen while an uncached month loads. Swapping in the short
  // loading state would collapse the page and lose the user's scroll position.
  readonly categoryPageQuery = injectQuery(() => ({
    ...this.groupService.groupPageQueryOptions(this.currentMonthYear()),
    placeholderData: keepPreviousData,
  }));
  readonly groupPage = computed(() => this.categoryPageQuery.data() ?? null);
  readonly loading = computed(() => this.categoryPageQuery.isPending());
  readonly showingPreviousMonth = computed(() => this.categoryPageQuery.isPlaceholderData());
  // Secondary month data only stands in while the whole page is dimmed. Once the new
  // month's groups render, a still-loading chart or count shows empty, not last month's.
  private readonly previousMonthPlaceholder = computed(() =>
    this.showingPreviousMonth() ? keepPreviousData : undefined,
  );
  readonly refreshing = computed(() => !!this.categoryPageQuery.data() && this.categoryPageQuery.isFetching());
  readonly categories = computed(() => this.groupPage()?.data ?? []);
  readonly filteredCategories = computed(() => this.filterCategories(this.categories(), this.groupSearch()));
  /** Transfers are a system-managed concept (LCL-66): pulled out of the groups and shown as a flat card behind the filter. */
  readonly showTransfers = signal(readStoredShowTransfers());
  /** Session-only lens (not persisted): narrows the groups to over-budget buckets. */
  readonly onlyOverBudget = signal(false);
  readonly hasActiveBudgetFilter = computed(() => this.showTransfers() || this.onlyOverBudget());
  readonly transfersBucket = computed(
    () =>
      this.categories()
        .flatMap(category => category.buckets)
        .find(bucket => bucket.type === 'TRANSFERS' && !bucket.archivedMonth) ?? null,
  );
  readonly transfersTransactionsQuery = injectQuery(() => {
    const transfersBucket = this.transfersBucket();
    return {
      ...this.transactionService.bucketTransactionsQueryOptions(
        transfersBucket?.id ?? '',
        this.currentMonthYear(),
        1,
        1,
      ),
      enabled: !!transfersBucket && this.showTransfers() && !this.groupService.reordering,
      placeholderData: this.previousMonthPlaceholder(),
    };
  });
  readonly transfersTransactionCount = computed(() => this.transfersTransactionsQuery.data()?.totalRecords ?? 0);
  /** TRANSFERS stripped unconditionally — shared by the normal and reorder/edit paths so the system bucket never surfaces. */
  readonly categoriesWithoutTransfers = computed(() => {
    this.editSession();
    return this.filteredCategories().reduce<Group[]>((withoutTransfers, category) => {
      const buckets = category.buckets.filter(bucket => bucket.type !== 'TRANSFERS');
      if (!buckets.length && category.buckets.length > 0) return withoutTransfers;
      withoutTransfers.push({ ...category, buckets });
      return withoutTransfers;
    }, []);
  });
  readonly displayedCategories = computed(() =>
    this.categoriesWithoutTransfers().reduce<Group[]>((displayed, category) => {
      if (!this.onlyOverBudget()) {
        displayed.push(category);
        return displayed;
      }
      const buckets = category.buckets.filter(isOverBudgetBucket);
      if (!buckets.length) return displayed;
      displayed.push({ ...category, buckets });
      return displayed;
    }, []),
  );
  readonly spendingTrendQuery = injectQuery(() => ({
    ...this.groupService.groupSpendingTrendQueryOptions(this.currentMonthYear()),
    placeholderData: this.previousMonthPlaceholder(),
  }));
  readonly spendingTrend = computed(() => this.spendingTrendQuery.data() ?? []);
  readonly budget = computed(() => this.groupPage()?.budget ?? 0);
  readonly updateMonthlyBudgetMutation = injectMutation(() =>
    this.groupService.updateMonthlyBudgetMutationOptions(),
  );
  readonly updateAvailable = this.appUpdate.updateAvailable;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.month.showSelector = false;
      this.groupService.reordering = false;
      this.budgetBulkEdit.discard();
    });

    effect(() => {
      const requestedEditMode = this.requestedEditMode();
      if (requestedEditMode !== 'budgets') {
        const hasEditState = untracked(() => this.groupService.reordering || this.budgetBulkEdit.hasDraft());
        if (this.routeDrivenEditMode || hasEditState) this.stopEditing();
        return;
      }

      const groupPage = this.groupPage();
      const reordering = untracked(() => this.groupService.reordering);
      if (!groupPage || this.showingPreviousMonth() || reordering) return;
      this.routeDrivenEditMode = true;
      this.startEditing(groupPage);
    });
  }

  isSearching(): boolean {
    return this.searchOpen() || !!this.groupSearch();
  }

  pageScrollClass(): string {
    const classes = ['h-full', 'overflow-x-hidden', 'overflow-y-auto'];
    if (this.groupService.reordering) classes.push('pt-16');

    return classes.join(' ');
  }

  getEditIncome(groupPage: GroupPage): number {
    if (this.groupService.reordering && this.budgetBulkEdit.hasDraft())
      return Math.max(this.budgetBulkEdit.totals().income, 0);
    return Math.max(groupPage?.budgeted?.INCOME ?? 0, 0);
  }

  getEditExpenses(groupPage: GroupPage): number {
    if (this.groupService.reordering && this.budgetBulkEdit.hasDraft())
      return Math.max(this.budgetBulkEdit.totals().expenses, 0);
    return Math.max(groupPage?.budget ?? 0, 0);
  }

  getEditNet(groupPage: GroupPage): number {
    return this.getEditIncome(groupPage) - this.getEditExpenses(groupPage);
  }

  isOverBudget(groupPage: GroupPage): boolean {
    return this.getEditNet(groupPage) < 0;
  }

  getBudgetStatusLabel(groupPage: GroupPage): string {
    return this.isOverBudget(groupPage) ? 'Over budget' : 'Under budget';
  }

  /** Phone edit bar: the full label doesn't fit beside Cancel and Save. */
  getBudgetStatusShortLabel(groupPage: GroupPage): string {
    return this.isOverBudget(groupPage) ? 'Over' : 'Under';
  }

  getBudgetStatusDigits(groupPage: GroupPage): string {
    return budgetVarianceDigits(this.getEditNet(groupPage));
  }

  ngOnInit() {
    this.month.showSelector = true;
    this.nav.title = 'budgets';
    // Proof-flow terminal step: reaching the budgets page. PostHog's
    // first-time-for-user math turns this into "first budget view".
    this.analytics.sendEvent(EVENT_BUDGETS_PAGE_VIEWED);

    this.month
      .getCurrentMonthYearFromParams$(this.route)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(monthYear => {
          this.month.setMonthYear(monthYear);
          this.currentMonthYear.set(monthYear);
        }),
      )
      .subscribe();
  }

  async doneReordering(): Promise<void> {
    // The draft holds the previous month's amounts until the new month loads.
    if (this.saving() || this.showingPreviousMonth()) return;
    const hadChanges = this.budgetBulkEdit.hasChanges();
    this.saving.set(true);
    this.saveError.set(null);
    try {
      if (hadChanges) {
        const categoriesList = this.categoriesList();
        if (!categoriesList) throw new Error('Budget list is not ready to save');
        await categoriesList.persistReorderRenames();
      }
    } catch {
      // Keep the draft so nothing typed is lost; the backend interceptor already
      // toasts the server's reason for 400s and connection failures.
      this.saveError.set("Your changes weren't saved. They're still here, so you can try again.");
      return;
    } finally {
      this.saving.set(false);
    }

    await this.exitEditing();
    if (hadChanges) this.toast.open('Budgets saved', 'OK', { tone: 'accent' });
    await this.categoryPageQuery.refetch();
  }

  cancelEditing(): void {
    if (this.saving()) return;
    if (!this.budgetBulkEdit.hasChanges()) {
      void this.exitEditing();
      return;
    }

    const data: ConfirmDialog = {
      message: 'Discard your changes to budgets?',
      cancelText: 'Keep editing',
      confirmText: 'Discard',
      confirmColor: 'warn',
      onConfirm: (): void => void this.exitEditing(),
    };
    this.dialog.open(ConfirmDialogComponent, { data });
  }

  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.groupService.reordering || event.defaultPrevented || event.isComposing) return;
    // Shortcuts belong to the page, not to an open discard or archive dialog.
    if (this.dialog.hasOpenDialog()) return;
    if (event.key === 'Escape' && !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      this.cancelEditing();
    } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void this.doneReordering();
    }
  }

  openBudgetEditing(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { edit: 'budgets' },
      queryParamsHandling: 'merge',
    });
  }

  // Private + page-required: bulk edit is entered only through the URL
  // (openBudgetEditing writes ?edit=budgets; the route effect drives this).
  // Keeping it out of reach of the template prevents wiring a button straight
  // to it and bypassing the route-backed state.
  private startEditing(groupPage: GroupPage): void {
    this.closeSearch();
    this.saveError.set(null);
    this.groupService.reordering = true;
    this.budgetBulkEdit.begin(groupPage);
    this.editSession.update(session => session + 1);
  }

  private stopEditing(): void {
    this.routeDrivenEditMode = false;
    this.saveError.set(null);
    this.groupService.reordering = false;
    this.budgetBulkEdit.discard();
    this.editSession.update(session => session + 1);
  }

  private async exitEditing(): Promise<void> {
    this.stopEditing();
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { edit: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  openSearch(): void {
    // The action row (and filter indicator) is hidden while searching — clear the
    // lens so search results aren't silently narrowed by an invisible filter.
    this.onlyOverBudget.set(false);
    this.analytics.sendEvent(EVENT_OPENED_CATEGORY_SEARCH);
    this.searchOpen.set(true);
  }

  updateApp(): Promise<void> {
    return this.appUpdate.applyUpdate();
  }

  clearSearch(): void {
    this.closeSearch();
  }

  setCategorySearch(search: string): void {
    this.groupService.groupSearch = search;
    this.groupSearch.set(search);
  }

  private closeSearch(): void {
    this.setCategorySearch('');
    this.searchOpen.set(false);
  }

  private getBudgetAmount(form: NgForm): number | null {
    const budget = form.controls['budget']?.value;
    if (budget === null || budget === undefined || budget === '') return null;
    return Number(budget);
  }

  editMonthlyBudget(form: NgForm) {
    const budget = this.getBudgetAmount(form);
    if (budget === null) return;
    this.updateMonthlyBudgetMutation.mutate(
      { budget, monthYear: this.currentMonthYear() },
      {
        onSuccess: () => {
          form.resetForm();
          this.analytics.sendEvent(EVENT_CHANGED_BUDGET_AMOUNT);
        },
      },
    );
  }

  closeEditMonthlyBudget(form: NgForm) {
    form.resetForm({ budget: this.budget() });
  }

  matchBudget() {

    const groupPage = this.groupPage();
    if (!groupPage) return;
    this.updateMonthlyBudgetMutation.mutate({
      budget: groupPage.budgeted.INCOME,
      monthYear: this.currentMonthYear(),
    });
  }

  toggleShowTransfers(): void {
    const next = !this.showTransfers();
    this.showTransfers.set(next);
    writeStoredShowTransfers(next);
  }

  toggleOnlyOverBudget(): void {
    this.onlyOverBudget.update(only => !only);
  }

  openTransfersDrawer(bucket: Bucket): void {
    this.drawerService.openBucketDrawer(bucket.id);
  }

  filterCategories(categories: Group[], search: string): Group[] {
    const searchValue = search.trim().toLowerCase();
    if (!searchValue) return categories;

    return categories.reduce<Group[]>((matches, category) => {
      const buckets = category.buckets.filter(bucket => bucket.name.toLowerCase().includes(searchValue));
      if (buckets.length) matches.push({ ...category, buckets });
      return matches;
    }, []);
  }
}

export const SHOW_TRANSFERS_STORAGE_KEY = 'budgets.show-transfers';

function readStoredShowTransfers(): boolean {
  try {
    return localStorage.getItem(SHOW_TRANSFERS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeStoredShowTransfers(show: boolean): void {
  try {
    localStorage.setItem(SHOW_TRANSFERS_STORAGE_KEY, show ? '1' : '0');
  } catch {
    // Private-mode/storage-denied environments just lose persistence, not the toggle.
  }
}
